import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CaretLeft,
  Envelope,
  Phone,
  Calendar,
  User,
  Baby,
  ChalkboardTeacher,
  Notepad,
  ClockCounterClockwise,
  Users,
  CheckCircle,
  ArrowRight,
  CreditCard,
  CaretRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  addEnrollmentNote,
  convertEnrollmentToFamily,
  getEnrollmentRecord,
  updateEnrollmentStage,
} from "@/lib/enrollment";
import {
  STAGE_META,
  allowedNextStages,
  formatChildName,
  formatEnrollmentDate,
  formatPersonName,
  SOURCE_LABELS,
} from "@/lib/enrollmentTypes";
import { getBillingProfileForFamily } from "@/lib/billing";
import { isSupabaseConfigured } from "@/lib/supabase";
import StageBadge from "@/components/enrollment/StageBadge";
import DocumentChecklist from "@/components/enrollment/DocumentChecklist";
import ReadinessStrip from "@/components/enrollment/ReadinessStrip";
import AdmissionWorkflowPanel from "@/components/admissions/AdmissionWorkflowPanel";

const PERSON_DEMO = {
  "demo-admin": "Priya Mehta",
  "demo-teacher": "Amara Johnson",
  "demo-parent": "Rivka Cohen",
};

export default function AdminEnrollmentDetail() {
  const { id } = useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [converting, setConverting] = useState(false);
  const [billingProfile, setBillingProfile] = useState(null);
  const [docsSummary, setDocsSummary] = useState(null);
  const [stageNote, setStageNote] = useState("");
  const [offerExpiresAt, setOfferExpiresAt] = useState("");
  const [waitlistPriority, setWaitlistPriority] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await getEnrollmentRecord(id);
      setRec(r);
    } catch (err) {
      toast.error(err.message || "Could not load record");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Resolve billing profile (if a family has been linked already)
  useEffect(() => {
    if (!rec?.linked_family_id || !isSupabaseConfigured) {
      setBillingProfile(null);
      return;
    }
    (async () => {
      try {
        const bp = await getBillingProfileForFamily(rec.linked_family_id);
        setBillingProfile(bp);
      } catch {
        setBillingProfile(null);
      }
    })();
  }, [rec?.linked_family_id]);

  const onConvert = async () => {
    if (!rec) return;
    const confirmText =
      rec.stage === "enrolled"
        ? "Re-run conversion? This is idempotent and will re-use the existing family/child if already linked."
        : "Create a new family and child record from this application? This will stamp the record as enrolled.";
    if (!window.confirm(confirmText)) return;
    setConverting(true);
    try {
      const res = await convertEnrollmentToFamily(id);
      if (res?.created_new) {
        toast.success("Active family and child record created.");
      } else {
        toast.success("Already linked — showing the existing family record.");
      }
      await load();
    } catch (err) {
      toast.error(err.message || "Could not convert enrollment");
    } finally {
      setConverting(false);
    }
  };

  const onChangeStage = async (nextStage) => {
    if (!rec || nextStage === rec.stage) return;
    setSaving(true);
    try {
      const updated = await updateEnrollmentStage({
        id,
        stage: nextStage,
        profileId: profile.id,
        expectedUpdatedAt: rec.updated_at,
        note: stageNote.trim() || null,
        offerExpiresAt: nextStage === "offer" ? offerExpiresAt || null : null,
        waitlistPriority: nextStage === "waitlisted" && waitlistPriority !== ""
          ? Number(waitlistPriority)
          : null,
      });
      // Preserve notes/history we already fetched, just replace the record fields.
      setRec((prev) => ({ ...prev, ...updated }));
      toast.success(`Stage set to ${STAGE_META[nextStage].label}`);
      setStageNote("");
      if (nextStage === "offer") setOfferExpiresAt("");
      if (nextStage === "waitlisted") setWaitlistPriority("");
      // Re-fetch full record to pull updated history.
      await load();
    } catch (err) {
      toast.error(err.message || "Could not change stage");
    } finally {
      setSaving(false);
    }
  };

  const onAddNote = async (e) => {
    e.preventDefault();
    const body = newNote.trim();
    if (!body) return;
    setAddingNote(true);
    try {
      const n = await addEnrollmentNote({
        enrollmentId: id,
        authorId: profile.id,
        body,
      });
      setRec((prev) => ({
        ...prev,
        notes: [n, ...(prev.notes || [])],
      }));
      setNewNote("");
      toast.success("Note added");
    } catch (err) {
      toast.error(err.message || "Could not add note");
    } finally {
      setAddingNote(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (!rec) {
    return (
      <div className="py-16 text-center text-sm text-neutral-500">
        Record not found.{" "}
        <button
          className="underline"
          onClick={() => navigate("/admin/enrollment")}
        >
          Back to pipeline
        </button>
      </div>
    );
  }

  const history = rec.history || [];
  const nextStages = allowedNextStages(rec.stage);
  const stageOptions = [rec.stage, ...nextStages];

  return (
    <div data-testid="admin-enrollment-detail" data-stage={rec.stage} className="space-y-6">
      <Link
        to="/admin/enrollment"
        data-testid="enr-detail-back"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
      >
        <CaretLeft size={16} weight="bold" />
        Back to pipeline
      </Link>

      <Link
        to="/admin/admissions"
        className="-mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-hover hover:underline"
      >
        Admissions workspace
        <CaretRight size={14} weight="bold" />
      </Link>

      {/* Header */}
      <header className="rounded-2xl border border-neutral-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
              Prospective enrollment
            </p>
            <h1 className="mt-1 font-heading text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl">
              {formatChildName(rec)}
            </h1>
            <p className="mt-1 text-sm text-neutral-600">
              {formatPersonName(rec)}
            </p>
          </div>
          <StageBadge stage={rec.stage} size="sm" />
        </div>
      </header>

      {/* Convert-to-family action + linked banner */}
      {isSupabaseConfigured && <ConversionPanel
        rec={rec}
        converting={converting}
        onConvert={onConvert}
      />}

      {isSupabaseConfigured && (
        <ReadinessStrip
          enrolled={rec.stage === "enrolled"}
          converted={Boolean(rec.linked_family_id && rec.linked_child_id)}
          docsReady={Boolean(
            docsSummary &&
              docsSummary.missingRequired === 0 &&
              docsSummary.rejectedCount === 0 &&
              docsSummary.totalRequired > 0,
          )}
          billingProfileReady={Boolean(billingProfile?.id)}
          docsSummary={docsSummary}
        />
      )}

      {isSupabaseConfigured && rec.linked_family_id && billingProfile?.id && (
        <Link
          to={`/admin/billing/${rec.linked_family_id}`}
          data-testid="enr-open-billing"
          className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-4 py-2 text-sm font-semibold text-primary-hover hover:bg-primary/5"
        >
          <CreditCard size={14} weight="duotone" />
          Open billing for this family
        </Link>
      )}

      {/* Stage control */}
      <section
        data-testid="enr-stage-control"
        className="rounded-2xl border border-neutral-200 bg-white p-5"
      >
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Change stage
        </p>
        <div className="flex flex-wrap gap-2">
          {stageOptions.map((s) => {
            const meta = STAGE_META[s];
            const selected = s === rec.stage;
            return (
              <button
                key={s}
                type="button"
                data-testid={`enr-stage-btn-${s}`}
                onClick={() => onChangeStage(s)}
                disabled={saving || selected}
                className={`rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-all ${
                  selected
                    ? "border-primary bg-primary/10 text-primary-hover cursor-default"
                    : "border-neutral-200 bg-white text-neutral-700 hover:border-primary/50 hover:bg-primary/5"
                } disabled:opacity-60`}
              >
                {meta.short}
              </button>
            );
          })}
        </div>
        <div className="mt-4 grid gap-3 lg:grid-cols-3">
          <label className="block lg:col-span-3">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.13em] text-neutral-500">Transition note (optional)</span>
            <input
              data-testid="admission-stage-note"
              value={stageNote}
              onChange={(event) => setStageNote(event.target.value)}
              maxLength={4000}
              placeholder="Why is this moving forward?"
              className="min-h-10 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </label>
          {nextStages.includes("offer") && (
            <label className="block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.13em] text-neutral-500">Offer review date (optional)</span>
              <input
                data-testid="admission-offer-expires"
                type="date"
                value={offerExpiresAt}
                onChange={(event) => setOfferExpiresAt(event.target.value)}
                className="min-h-10 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>
          )}
          {nextStages.includes("waitlisted") && (
            <label className="block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.13em] text-neutral-500">Center queue priority (optional)</span>
              <input
                data-testid="admission-waitlist-priority"
                type="number"
                value={waitlistPriority}
                onChange={(event) => setWaitlistPriority(event.target.value)}
                placeholder="0"
                className="min-h-10 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>
          )}
        </div>
        <p className="mt-3 text-xs text-neutral-500">
          Only valid next steps are shown. HoneyAcorn records the change and may queue the appropriate transactional update when the case has an email on file; it never creates an offer automatically.
        </p>
      </section>

      {isSupabaseConfigured && (
        <AdmissionWorkflowPanel enrollment={rec} onChanged={load} />
      )}

      {/* Family + child details */}
      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card testId="enr-family-card" title="Family">
          <Row icon={User} label="Name" value={formatPersonName(rec)} />
          <Row icon={Envelope} label="Email" value={rec.parent_email || "—"} />
          <Row icon={Phone} label="Phone" value={rec.parent_phone || "—"} />
          <Row
            icon={Calendar}
            label="Source"
            value={SOURCE_LABELS[rec.source] || rec.source || "—"}
          />
          <Row
            icon={Calendar}
            label="Submitted"
            value={formatEnrollmentDate(rec.created_at)}
          />
        </Card>
        <Card testId="enr-child-card" title="Child">
          <Row icon={Baby} label="Name" value={formatChildName(rec)} />
          <Row
            icon={Calendar}
            label="Date of birth"
            value={formatEnrollmentDate(rec.child_date_of_birth)}
          />
          <Row
            icon={ChalkboardTeacher}
            label="Classroom"
            value={
              rec.classroom?.name ||
              rec.preferred_age_group ||
              "No preference"
            }
          />
          <Row
            icon={Calendar}
            label="Preferred start"
            value={formatEnrollmentDate(rec.preferred_start_date)}
          />
        </Card>
      </section>

      {rec.parent_notes && (
        <section className="rounded-2xl border border-neutral-200 bg-white p-5">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Parent notes (visible to family)
          </p>
          <p
            data-testid="enr-parent-notes"
            className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-800"
          >
            {rec.parent_notes}
          </p>
        </section>
      )}

      {/* Internal notes */}
      <section
        data-testid="enr-internal-notes"
        className="rounded-2xl border border-neutral-200 bg-white p-5"
      >
        <div className="mb-3 flex items-center gap-2">
          <Notepad size={18} weight="duotone" className="text-primary-hover" />
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Internal notes · admin-only
          </p>
        </div>
        <form onSubmit={onAddNote} className="space-y-3">
          <textarea
            data-testid="enr-note-input"
            rows={3}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Private note — not visible to the family."
            className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              data-testid="enr-note-submit"
              disabled={addingNote || !newNote.trim()}
              className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-50"
            >
              {addingNote ? "Adding…" : "Add note"}
            </button>
          </div>
        </form>
        <ul className="mt-4 space-y-2" data-testid="enr-notes-list">
          {(rec.notes || []).length === 0 ? (
            <li className="rounded-xl bg-neutral-50 px-3 py-2 text-xs text-neutral-500">
              No internal notes yet.
            </li>
          ) : (
            rec.notes.map((n) => {
              const author =
                n.author?.full_name ||
                n.author?.email ||
                PERSON_DEMO[n.author_id] ||
                "Admin";
              return (
                <li
                  key={n.id}
                  className="rounded-xl border border-neutral-200 bg-white p-3"
                >
                  <p className="whitespace-pre-wrap text-sm text-neutral-800">
                    {n.body}
                  </p>
                  <p className="mt-1 text-[11px] text-neutral-500">
                    {author} · {formatEnrollmentDate(n.created_at)}
                  </p>
                </li>
              );
            })
          )}
        </ul>
      </section>

      {/* Stage history */}
      <section
        data-testid="enr-stage-history"
        className="rounded-2xl border border-neutral-200 bg-white p-5"
      >
        <div className="mb-3 flex items-center gap-2">
          <ClockCounterClockwise
            size={18}
            weight="duotone"
            className="text-primary-hover"
          />
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Pipeline history
          </p>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-neutral-500">No transitions yet.</p>
        ) : (
          <ol className="space-y-2">
            {history.map((h, i) => {
              const to = STAGE_META[h.to_stage];
              const from = h.from_stage ? STAGE_META[h.from_stage] : null;
              return (
                <li
                  key={h.id || i}
                  className="rounded-xl bg-neutral-50 px-3 py-2 text-sm"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-neutral-800">
                      {from ? (
                        <>
                          <span className="text-neutral-500">{from.short}</span>
                          {" → "}
                        </>
                      ) : (
                        <span className="text-neutral-500">Created · </span>
                      )}
                      <span className="font-semibold">{to?.short}</span>
                    </span>
                    <span className="shrink-0 text-xs text-neutral-500">
                      {formatEnrollmentDate(h.changed_at)}
                    </span>
                  </div>
                  {h.note && <p className="mt-1 text-xs leading-relaxed text-neutral-600">{h.note}</p>}
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {isSupabaseConfigured && (
        <DocumentChecklist
          testId="admin-document-checklist"
          enrollment={rec}
          organizationId={rec.organization_id}
          role="admin"
          canUpload
          uploadedBy={profile?.id}
          onSummaryChange={setDocsSummary}
        />
      )}
    </div>
  );
}

function ConversionPanel({ rec, converting, onConvert }) {
  const alreadyLinked = Boolean(rec.linked_family_id && rec.linked_child_id);
  const canConvertStage = ["offer", "enrollment_pending", "enrolled", "accepted"].includes(rec.stage);

  if (alreadyLinked) {
    return (
      <section
        data-testid="enr-conversion-linked"
        className="rounded-2xl border border-primary/30 bg-primary/5 p-5"
      >
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary-hover">
            <CheckCircle size={20} weight="duotone" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-base font-semibold text-neutral-900">
              Active family & child linked
            </p>
            <p className="mt-1 text-sm text-neutral-700">
              This enrollment has been converted into the operational{" "}
              <span className="font-mono text-xs text-neutral-500">families</span>
              {" + "}
              <span className="font-mono text-xs text-neutral-500">children</span>
              {" "}records. Billing, attendance, and daily reports will attach here.
            </p>
            <dl className="mt-3 grid grid-cols-1 gap-y-1 text-sm sm:grid-cols-2">
              <LinkRow label="Family ID" value={rec.linked_family_id} testId="enr-linked-family-id" />
              <LinkRow label="Child ID" value={rec.linked_child_id} testId="enr-linked-child-id" />
            </dl>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to={`/admin/families/${rec.linked_family_id}`}
                data-testid="enr-open-family"
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98]"
              >
                Open family
                <CaretRight size={12} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!canConvertStage) {
    return null;
  }

  return (
    <section
      data-testid="enr-conversion-cta"
      className="rounded-2xl border border-secondary/50 bg-secondary/20 p-5"
    >
      <div className="flex flex-wrap items-start gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white">
          <Users size={20} weight="duotone" className="text-primary-hover" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-base font-semibold text-neutral-900">
            Ready to convert to an active family?
          </p>
          <p className="mt-1 text-sm text-neutral-700">
            This will create a <span className="font-semibold">families</span> row, a{" "}
            <span className="font-semibold">children</span> row, and link the parent
            profile via <span className="font-semibold">family_members</span>. Stage
            will advance to <span className="font-semibold">enrolled</span>. The action
            is idempotent — if already linked, nothing is duplicated.
          </p>
        </div>
        <button
          type="button"
          data-testid="enr-convert-button"
          onClick={onConvert}
          disabled={converting}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98] disabled:opacity-60"
        >
          {converting ? "Converting…" : "Convert to Active Family"}
          {!converting && <ArrowRight size={14} weight="bold" />}
        </button>
      </div>
    </section>
  );
}

function LinkRow({ label, value, testId }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="w-24 flex-shrink-0 text-[11px] font-bold uppercase tracking-[0.12em] text-neutral-500">
        {label}
      </dt>
      <dd
        data-testid={testId}
        className="truncate font-mono text-[11px] text-neutral-700"
        title={value}
      >
        {value}
      </dd>
    </div>
  );
}

function Card({ title, children, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-2"
    >
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {title}
      </p>
      {children}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <Icon size={16} weight="duotone" className="text-primary-hover" />
      <span className="w-28 flex-shrink-0 text-xs font-semibold uppercase tracking-wider text-neutral-500">
        {label}
      </span>
      <span className="truncate text-sm text-neutral-800">{value}</span>
    </div>
  );
}
